import { termMarketsByChain } from '@1delta/data-sdk'
import type { TermMarketConfig } from '@1delta/data-sdk'
import { createTermBookSource } from './apiClient'
import { fetchTermStoreOrders, toTermFillNow } from './orderStore'
import type { TermStoreOrder } from './orderStore'
import type {
  TermAuctionWindow,
  TermBook,
  TermBookSource,
  TermBookTop,
  TermMarketRaw,
} from './types'

/** Levels kept per book side in the public batch (a chunk for later filtering). */
const TERM_BOOK_LEVELS = 20

/**
 * Last-known-good snapshot cache, keyed by `${chainId}:${termRepoId}`.
 * The Term subgraph can rate-limit / 5xx transiently; reusing the last good
 * snapshot within the window keeps a representative rate + book flowing
 * (display-only — direct actions size off live listing quotes, never this).
 */
const LKG_TTL_SEC = 30 * 60
const lastGood = new Map<
  string,
  {
    top: TermBookTop
    book: TermBook | null
    auction: TermAuctionWindow | null
    at: number
  }
>()

async function fetchTopAndBookWithFallback(
  source: TermBookSource,
  chainId: string,
  config: TermMarketConfig,
  nowSec: number,
): Promise<{
  top: TermBookTop | null
  book: TermBook | null
  auction: TermAuctionWindow | null
}> {
  const key = `${chainId}:${config.termRepoId}`

  // Prefer the one-query top+book+auction; fall back to top-only for stub
  // sources (which expose no auction round).
  let fresh: {
    top: TermBookTop
    book: TermBook
    auction: TermAuctionWindow | null
  } | null = null
  if (source.getTopAndBook) {
    fresh = await source
      .getTopAndBook(config, TERM_BOOK_LEVELS)
      .catch(() => null)
  } else {
    const top = await source.getBookTop(config).catch(() => null)
    if (top) fresh = { top, book: { bids: [], asks: [] }, auction: null }
  }

  if (fresh) {
    lastGood.set(key, {
      top: fresh.top,
      book: fresh.book,
      auction: fresh.auction ?? null,
      at: nowSec,
    })
    return { top: fresh.top, book: fresh.book, auction: fresh.auction ?? null }
  }
  const cached = lastGood.get(key)
  if (cached && nowSec - cached.at <= LKG_TTL_SEC) {
    // A cached auction that has since closed must NOT be replayed as live —
    // the whole point of the field is that it gates borrow actions.
    const stillOpen =
      cached.auction && cached.auction.endTime > nowSec ? cached.auction : null
    return { top: cached.top, book: cached.book, auction: stillOpen }
  }
  return { top: null, book: null, auction: null }
}

/**
 * Fetch the current top-of-book + a bounded book chunk for every configured
 * Term repo on a chain.
 *
 * Public data = static repo config (from the `termMarkets` registry) + live
 * secondary-listing / auction rates + depth (from the subgraph source). The
 * aggregate best rate + FULL depth live on `top`; `book` carries the best
 * `TERM_BOOK_LEVELS` open orders per side for downstream filtering. Both are
 * null when the fetch failed and no recent snapshot is cached, and when no data
 * endpoint is configured (rates fall back to 0).
 */
export async function fetchTermMarkets(
  chainId: string,
  source: TermBookSource = createTermBookSource(chainId),
  fetchOrders: (
    chainId: string,
  ) => Promise<Map<string, TermStoreOrder[]> | null> = fetchTermStoreOrders,
): Promise<TermMarketRaw[]> {
  const markets = termMarketsByChain(chainId)
  if (markets.length === 0) return []

  const nowSec = Math.floor(Date.now() / 1000)
  // ONE order-store request covers the whole chain; orders key on the repo's
  // SERVICER, not termRepoId. Null = store unreachable (rows degrade to the
  // auction/secondary picture rather than reading "no fill-now liquidity").
  const storeOrders = await fetchOrders(chainId).catch(() => null)
  return Promise.all(
    markets.map(async (config) => {
      if (Number(config.maturity) <= nowSec) {
        return { config, top: null, book: null, auction: null, fillNow: null }
      }
      const { top, book, auction } = await fetchTopAndBookWithFallback(
        source,
        chainId,
        config,
        nowSec,
      )
      const fillNow = storeOrders
        ? toTermFillNow(
            storeOrders.get(config.servicer.toLowerCase()),
            config.loanDecimals,
            nowSec,
          )
        : null
      return { config, top, book, auction, fillNow }
    }),
  )
}
